/**
 * suv/computeRoiPsmaVolumes.js
 *
 * SUV stats + PSMA-VOL for the ROIs of one structure set (one CT-PET pair).
 * Reads stage-1 results only (redux stats + sample cache); no re-sampling.
 * Used by save (getRTStructDatasetWithMeasurement), the overlay and the
 * review modal.
 *
 * Under RECIP 1.0 every ROI gets an entry: an ROI without stage-1 stats
 * (not a PET-CT pair, SearchRegion, any stage-1 NE) is written as explicit
 * NE ({ suv: null, psma_volume: null }), never left out.
 */

import { makeSuvKey, getSuvStats } from './suvStore';
import { getSuvSamples } from './suvSampleCache';
import {
  findRefLiverROI,
  computePsmaThresholds,
  computePsmaVolume,
} from './psmaVolume';

/** Thresholds of one structure set, from its single REF-LIVER ROI. */
export function computeStructureSetThresholds({
  structureSetUID,
  ctSeriesUID,
  rois = [],
  links = [],
}) {
  const { roi: refRoi, reason } = findRefLiverROI(rois, links);
  if (!refRoi) return computePsmaThresholds(null, reason);
  const key = makeSuvKey(structureSetUID, refRoi.ROINumber, ctSeriesUID);
  return computePsmaThresholds(getSuvStats(key));
}

/** { suv, psma_volume } of one ROI, given the structure set thresholds. */
export function computeRoiPsmaEntry({
  structureSetUID,
  ctSeriesUID,
  ROINumber,
  thresholds,
}) {
  const key = makeSuvKey(structureSetUID, ROINumber, ctSeriesUID);
  const stats = getSuvStats(key);
  if (!stats || stats.ne) return { suv: null, psma_volume: null };
  return {
    suv: {
      max: stats.suvMax,
      mean: stats.suvMean,
      min: stats.suvMin,
      sd: stats.suvSD,
    },
    psma_volume: computePsmaVolume(getSuvSamples(key), thresholds),
  };
}

/**
 * @returns {{ thresholds: object, byROINumber: Map<number, { suv, psma_volume }> }}
 */
export default function computeRoiPsmaVolumes({
  structureSetUID,
  ctSeriesUID,
  rois = [],
  links = [],
}) {
  const thresholds = computeStructureSetThresholds({
    structureSetUID,
    ctSeriesUID,
    rois,
    links,
  });
  const byROINumber = new Map();
  for (const roi of rois) {
    byROINumber.set(
      roi.ROINumber,
      computeRoiPsmaEntry({
        structureSetUID,
        ctSeriesUID,
        ROINumber: roi.ROINumber,
        thresholds,
      })
    );
  }
  return { thresholds, byROINumber };
}